import * as Sentry from "@sentry/react";
import { Component, type ErrorInfo, type ReactNode } from "react";

import ErrorComponent from "@/components/ui/error";

interface Props {
  children: ReactNode;
  onSwitchToListView: () => void;
  className?: string;
}

interface State {
  hasError: boolean;
}

/**
 * Error boundary for Google Maps load failures.
 * When the map fails (e.g. CSP blocks maps.googleapis.com, ad blocker,
 * network issues), shows a fallback with option to switch to list view.
 */
export class MapErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      tags: { component: "MapErrorBoundary", mapLoadFailure: "true" },
      contexts: {
        react: { componentStack: errorInfo.componentStack },
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className={this.props.className}
          style={{ minHeight: 200, display: "flex", alignItems: "center" }}
        >
          <ErrorComponent
            title="Map unavailable"
            message="The map could not load. This can happen if an ad blocker is blocking Google Maps, due to network issues, or Content Security Policy restrictions. Try using the list view instead."
            action={{
              label: "Switch to list view",
              onClick: () => {
                this.setState({ hasError: false });
                this.props.onSwitchToListView();
              },
            }}
          />
        </div>
      );
    }

    return this.props.children;
  }
}
